export enum EnrichmentExportScope {
  ALL = "ALL",
  SINCE_LAST_EXPORT = "SINCE_LAST_EXPORT",
  CUSTOM = "CUSTOM",
}

export enum EnrichmentExportOutputFormat {
  JSON = "JSON",
  CSV = "CSV",
  XLSX = "XLSX",
}

/** Mirrors backend EnrichmentExportParamsDTO */
export type EnrichmentExportParams = {
  scope: EnrichmentExportScope;
  outputFormat: EnrichmentExportOutputFormat;
  dateFrom?: string;
  dateTo?: string;
  includeChildren?: boolean;
};

export type EnrichmentExportFormValues = {
  scope: EnrichmentExportScope;
  outputFormat: EnrichmentExportOutputFormat;
  dateFrom: string;
  dateTo: string;
  includeChildren: boolean;
};

export const getEnrichmentExportInitialValues = (): EnrichmentExportFormValues => ({
  scope: EnrichmentExportScope.CUSTOM,
  outputFormat: EnrichmentExportOutputFormat.JSON,
  dateFrom: "",
  dateTo: "",
  includeChildren: false,
});


export type EnrichmentFieldTier =
  | "INCLUDED"
  | "CONSENT_FOR_FULL_RESOLUTION"
  | "EXPLICIT_CONSENT_REQUIRED";

export interface EnrichmentFieldEntry {
  tier: EnrichmentFieldTier;
  enabled: boolean;
}

export interface BrandEnrichmentConfig {
  tiers: Record<EnrichmentFieldTier, boolean>;
  fields: Record<string, EnrichmentFieldEntry>;
}

// ─── Complete field → consent tier map ───────────────────────────────────────

export const ENRICHMENT_FIELD_TIER_MAP: Record<string, EnrichmentFieldTier> = {
  // ── Person Profile — Basic Information ─────────────────────────────────────
  id: "INCLUDED",
  first_name: "EXPLICIT_CONSENT_REQUIRED",
  middle_name: "EXPLICIT_CONSENT_REQUIRED",
  middle_initial: "EXPLICIT_CONSENT_REQUIRED",
  last_name: "EXPLICIT_CONSENT_REQUIRED",
  last_initial: "EXPLICIT_CONSENT_REQUIRED",
  full_name: "EXPLICIT_CONSENT_REQUIRED",
  headline: "CONSENT_FOR_FULL_RESOLUTION",
  job_title: "CONSENT_FOR_FULL_RESOLUTION",
  primary_email: "EXPLICIT_CONSENT_REQUIRED",
  email_status: "INCLUDED",
  photo_url: "EXPLICIT_CONSENT_REQUIRED",
  extrapolated_email_confidence: "INCLUDED",
  sex: "EXPLICIT_CONSENT_REQUIRED",
  birth_date: "EXPLICIT_CONSENT_REQUIRED",
  birth_year: "EXPLICIT_CONSENT_REQUIRED",
  interests: "CONSENT_FOR_FULL_RESOLUTION",
  skills: "CONSENT_FOR_FULL_RESOLUTION",
  industry: "INCLUDED",
  seniority: "CONSENT_FOR_FULL_RESOLUTION",
  functions: "INCLUDED",
  departments: "INCLUDED",
  subdepartments: "INCLUDED",
  intent_strength: "INCLUDED",
  show_intent: "INCLUDED",
  is_likely_to_engage: "INCLUDED",
  revealed_for_current_team: "INCLUDED",
  contact_id: "CONSENT_FOR_FULL_RESOLUTION",
  dataset_version: "INCLUDED",
  home_owner: "CONSENT_FOR_FULL_RESOLUTION",
  married: "CONSENT_FOR_FULL_RESOLUTION",
  children: "CONSENT_FOR_FULL_RESOLUTION",
  device_type: "INCLUDED",
  profile_alias: "CONSENT_FOR_FULL_RESOLUTION",
  person: "CONSENT_FOR_FULL_RESOLUTION",

  // ── Location ───────────────────────────────────────────────────────────────
  location_name: "CONSENT_FOR_FULL_RESOLUTION",
  location_locality: "CONSENT_FOR_FULL_RESOLUTION",
  location_region: "CONSENT_FOR_FULL_RESOLUTION",
  location_country: "CONSENT_FOR_FULL_RESOLUTION",
  location_continent: "INCLUDED",
  location_postal_code: "CONSENT_FOR_FULL_RESOLUTION",
  location_street_address: "EXPLICIT_CONSENT_REQUIRED",
  location_address_line_2: "EXPLICIT_CONSENT_REQUIRED",
  location_geo: "CONSENT_FOR_FULL_RESOLUTION",
  location_metro: "INCLUDED",
  location_last_updated: "INCLUDED",
  location_names: "CONSENT_FOR_FULL_RESOLUTION",
  regions: "CONSENT_FOR_FULL_RESOLUTION",
  countries: "CONSENT_FOR_FULL_RESOLUTION",
  street_addresses: "EXPLICIT_CONSENT_REQUIRED",
  formatted_address: "CONSENT_FOR_FULL_RESOLUTION",
  time_zone: "INCLUDED",

  // ── Employment History ─────────────────────────────────────────────────────
  experience: "CONSENT_FOR_FULL_RESOLUTION",
  job_title_role: "CONSENT_FOR_FULL_RESOLUTION",
  job_title_sub_role: "CONSENT_FOR_FULL_RESOLUTION",
  job_title_class: "CONSENT_FOR_FULL_RESOLUTION",
  job_title_levels: "INCLUDED",
  job_start_date: "CONSENT_FOR_FULL_RESOLUTION",
  job_last_changed: "INCLUDED",
  job_last_verified: "INCLUDED",
  education: "CONSENT_FOR_FULL_RESOLUTION",

  // ── Current Company ────────────────────────────────────────────────────────
  job_company_id: "INCLUDED",
  job_company_name: "INCLUDED",
  job_company_website: "INCLUDED",
  job_company_size: "INCLUDED",
  job_company_founded: "INCLUDED",
  job_company_industry: "INCLUDED",
  job_company_linkedin_url: "INCLUDED",
  job_company_linkedin_id: "INCLUDED",
  job_company_facebook_url: "INCLUDED",
  job_company_twitter_url: "INCLUDED",
  job_company_location_name: "INCLUDED",
  job_company_location_locality: "INCLUDED",
  job_company_location_metro: "INCLUDED",
  job_company_location_region: "INCLUDED",
  job_company_location_country: "INCLUDED",
  job_company_location_continent: "INCLUDED",
  job_company_location_postal_code: "INCLUDED",
  job_company_location_street_address: "INCLUDED",
  job_company_location_address_line_2: "INCLUDED",
  job_company_location_geo: "INCLUDED",

  // ── Organization ───────────────────────────────────────────────────────────
  org_id: "INCLUDED",
  org_name: "INCLUDED",
  org_website_url: "INCLUDED",
  org_blog_url: "INCLUDED",
  org_phone: "INCLUDED",
  org_founded_year: "INCLUDED",
  org_description: "INCLUDED",
  org_short_description: "INCLUDED",
  org_seo_description: "INCLUDED",
  org_logo_url: "INCLUDED",
  org_angellist_url: "INCLUDED",
  org_linkedin_url: "INCLUDED",
  org_linkedin_id: "INCLUDED",
  org_twitter_url: "INCLUDED",
  org_facebook_url: "INCLUDED",
  org_publicly_traded_symbol: "INCLUDED",
  org_publicly_traded_exchange: "INCLUDED",
  org_annual_revenue: "INCLUDED",
  org_annual_revenue_printed: "INCLUDED",
  org_total_funding: "INCLUDED",
  org_total_funding_printed: "INCLUDED",
  org_latest_funding_stage: "INCLUDED",
  org_latest_funding_date: "INCLUDED",
  org_raw_address: "INCLUDED",
  org_street_address: "INCLUDED",
  org_city: "INCLUDED",
  org_state: "INCLUDED",
  org_postal_code: "INCLUDED",
  org_country: "INCLUDED",
  org_location_geo: "INCLUDED",
  org_technology_names: "INCLUDED",
  org_current_technologies: "INCLUDED",
  org_industries: "INCLUDED",
  org_secondary_industries: "INCLUDED",
  org_sic_codes: "INCLUDED",
  org_naics_codes: "INCLUDED",
  org_alexa_ranking: "INCLUDED",
  org_keywords: "INCLUDED",
  org_headcount_growth_6m: "INCLUDED",
  org_headcount_growth_12m: "INCLUDED",
  org_headcount_growth_24m: "INCLUDED",
  org_owned_by_id: "INCLUDED",
  org_owned_by_name: "INCLUDED",
  org_num_suborganizations: "INCLUDED",
  org_funding_events: "INCLUDED",
  org_estimated_num_employees: "INCLUDED",
  org_primary_domain: "INCLUDED",

  // ── Contact Information ────────────────────────────────────────────────────
  work_email: "EXPLICIT_CONSENT_REQUIRED",
  recommended_personal_email: "EXPLICIT_CONSENT_REQUIRED",
  personal_emails: "EXPLICIT_CONSENT_REQUIRED",
  contact_emails: "EXPLICIT_CONSENT_REQUIRED",
  contact_email_status: "EXPLICIT_CONSENT_REQUIRED",
  contact_email_sha256: "EXPLICIT_CONSENT_REQUIRED",
  contact_email_md5: "EXPLICIT_CONSENT_REQUIRED",
  contact_email_free_domain: "INCLUDED",
  contact_email_position: "INCLUDED",
  mobile_phone: "EXPLICIT_CONSENT_REQUIRED",
  phone_numbers: "EXPLICIT_CONSENT_REQUIRED",
  phone_dnc_status: "INCLUDED",
  phone_dialer_flags: "INCLUDED",
  phone_country_name: "INCLUDED",
  phone_country_enabled: "INCLUDED",
  phone_potential_high_risk: "INCLUDED",

  // ── Online Profiles / Social ───────────────────────────────────────────────
  profiles: "EXPLICIT_CONSENT_REQUIRED",
  linkedin_url: "EXPLICIT_CONSENT_REQUIRED",
  linkedin_username: "EXPLICIT_CONSENT_REQUIRED",
  linkedin_id: "EXPLICIT_CONSENT_REQUIRED",
  facebook_url: "EXPLICIT_CONSENT_REQUIRED",
  facebook_username: "EXPLICIT_CONSENT_REQUIRED",
  facebook_id: "EXPLICIT_CONSENT_REQUIRED",
  twitter_url: "EXPLICIT_CONSENT_REQUIRED",
  twitter_username: "EXPLICIT_CONSENT_REQUIRED",
  github_url: "EXPLICIT_CONSENT_REQUIRED",
  github_username: "EXPLICIT_CONSENT_REQUIRED",
};

export const TIER_LABELS: Record<EnrichmentFieldTier, string> = {
  INCLUDED: "Included",
  CONSENT_FOR_FULL_RESOLUTION: "Consent for Full Resolution",
  EXPLICIT_CONSENT_REQUIRED: "Explicit Consent Required",
};

// ─── Field type tag ──────────────────────────────────────────────────────────

export type FieldType =
  | "Identifier"
  | "Identity"
  | "Professional"
  | "Contact"
  | "Data Quality"
  | "Social"
  | "Demographic"
  | "Behavioral"
  | "Firmographic"
  | "Metadata"
  | "Location"
  | "Employment"
  | "Education"
  | "Compliance";

export const FIELD_TYPE_MAP: Record<string, FieldType> = {
  // Person Profile
  id: "Identifier",
  first_name: "Identity",
  middle_name: "Identity",
  middle_initial: "Identity",
  last_name: "Identity",
  last_initial: "Identity",
  full_name: "Identity",
  headline: "Professional",
  job_title: "Professional",
  primary_email: "Contact",
  email_status: "Data Quality",
  photo_url: "Identity",
  extrapolated_email_confidence: "Data Quality",
  sex: "Demographic",
  birth_date: "Demographic",
  birth_year: "Demographic",
  interests: "Behavioral",
  skills: "Professional",
  industry: "Firmographic",
  seniority: "Professional",
  functions: "Professional",
  departments: "Professional",
  subdepartments: "Professional",
  intent_strength: "Behavioral",
  show_intent: "Behavioral",
  is_likely_to_engage: "Behavioral",
  revealed_for_current_team: "Behavioral",
  contact_id: "Identifier",
  dataset_version: "Metadata",
  home_owner: "Demographic",
  married: "Demographic",
  children: "Demographic",
  device_type: "Metadata",
  profile_alias: "Identifier",
  person: "Identity",

  // Location
  location_name: "Location",
  location_locality: "Location",
  location_region: "Location",
  location_country: "Location",
  location_continent: "Location",
  location_postal_code: "Location",
  location_street_address: "Location",
  location_address_line_2: "Location",
  location_geo: "Location",
  location_metro: "Location",
  location_last_updated: "Metadata",
  location_names: "Location",
  regions: "Location",
  countries: "Location",
  street_addresses: "Location",
  formatted_address: "Location",
  time_zone: "Location",

  // Employment History
  experience: "Employment",
  job_title_role: "Professional",
  job_title_sub_role: "Professional",
  job_title_class: "Professional",
  job_title_levels: "Professional",
  job_start_date: "Employment",
  job_last_changed: "Metadata",
  job_last_verified: "Metadata",
  education: "Education",

  // Current Company
  job_company_id: "Firmographic",
  job_company_name: "Firmographic",
  job_company_website: "Firmographic",
  job_company_size: "Firmographic",
  job_company_founded: "Firmographic",
  job_company_industry: "Firmographic",
  job_company_linkedin_url: "Firmographic",
  job_company_linkedin_id: "Firmographic",
  job_company_facebook_url: "Firmographic",
  job_company_twitter_url: "Firmographic",
  job_company_location_name: "Firmographic",
  job_company_location_locality: "Firmographic",
  job_company_location_metro: "Firmographic",
  job_company_location_region: "Firmographic",
  job_company_location_country: "Firmographic",
  job_company_location_continent: "Firmographic",
  job_company_location_postal_code: "Firmographic",
  job_company_location_street_address: "Firmographic",
  job_company_location_address_line_2: "Firmographic",
  job_company_location_geo: "Firmographic",

  // Organization
  org_id: "Firmographic",
  org_name: "Firmographic",
  org_website_url: "Firmographic",
  org_blog_url: "Firmographic",
  org_phone: "Firmographic",
  org_founded_year: "Firmographic",
  org_description: "Firmographic",
  org_short_description: "Firmographic",
  org_seo_description: "Firmographic",
  org_logo_url: "Firmographic",
  org_angellist_url: "Firmographic",
  org_linkedin_url: "Firmographic",
  org_linkedin_id: "Firmographic",
  org_twitter_url: "Firmographic",
  org_facebook_url: "Firmographic",
  org_publicly_traded_symbol: "Firmographic",
  org_publicly_traded_exchange: "Firmographic",
  org_annual_revenue: "Firmographic",
  org_annual_revenue_printed: "Firmographic",
  org_total_funding: "Firmographic",
  org_total_funding_printed: "Firmographic",
  org_latest_funding_stage: "Firmographic",
  org_latest_funding_date: "Firmographic",
  org_raw_address: "Firmographic",
  org_street_address: "Firmographic",
  org_city: "Firmographic",
  org_state: "Firmographic",
  org_postal_code: "Firmographic",
  org_country: "Firmographic",
  org_location_geo: "Firmographic",
  org_technology_names: "Firmographic",
  org_current_technologies: "Firmographic",
  org_industries: "Firmographic",
  org_secondary_industries: "Firmographic",
  org_sic_codes: "Firmographic",
  org_naics_codes: "Firmographic",
  org_alexa_ranking: "Firmographic",
  org_keywords: "Firmographic",
  org_headcount_growth_6m: "Firmographic",
  org_headcount_growth_12m: "Firmographic",
  org_headcount_growth_24m: "Firmographic",
  org_owned_by_id: "Firmographic",
  org_owned_by_name: "Firmographic",
  org_num_suborganizations: "Firmographic",
  org_funding_events: "Firmographic",
  org_estimated_num_employees: "Firmographic",
  org_primary_domain: "Firmographic",

  // Contact Information
  work_email: "Contact",
  recommended_personal_email: "Contact",
  personal_emails: "Contact",
  contact_emails: "Contact",
  contact_email_status: "Data Quality",
  contact_email_sha256: "Data Quality",
  contact_email_md5: "Data Quality",
  contact_email_free_domain: "Data Quality",
  contact_email_position: "Data Quality",
  mobile_phone: "Contact",
  phone_numbers: "Contact",
  phone_dnc_status: "Compliance",
  phone_dialer_flags: "Compliance",
  phone_country_name: "Metadata",
  phone_country_enabled: "Metadata",
  phone_potential_high_risk: "Compliance",

  // Online Profiles / Social
  profiles: "Social",
  linkedin_url: "Social",
  linkedin_username: "Social",
  linkedin_id: "Social",
  facebook_url: "Social",
  facebook_username: "Social",
  facebook_id: "Social",
  twitter_url: "Social",
  twitter_username: "Social",
  github_url: "Social",
  github_username: "Social",
};

export const TYPE_TAG_COLORS: Record<FieldType, { bg: string; text: string }> = {
  Identifier: { bg: "#E0F2FE", text: "#0284C7" },
  Identity: { bg: "#DBEAFE", text: "#1D4ED8" },
  Professional: { bg: "#F3E8FF", text: "#7C3AED" },
  Contact: { bg: "#FEE2E2", text: "#DC2626" },
  "Data Quality": { bg: "#E0E7FF", text: "#4F46E5" },
  Social: { bg: "#CFFAFE", text: "#0891B2" },
  Demographic: { bg: "#FEF3C7", text: "#D97706" },
  Behavioral: { bg: "#DCFCE7", text: "#16A34A" },
  Firmographic: { bg: "#E2E8F0", text: "#475569" },
  Metadata: { bg: "#F3F4F6", text: "#6B7280" },
  Location: { bg: "#CCFBF1", text: "#0D9488" },
  Employment: { bg: "#FFEDD5", text: "#EA580C" },
  Education: { bg: "#FCE7F3", text: "#DB2777" },
  Compliance: { bg: "#FFE4E6", text: "#E11D48" },
};

// ─── Category grouping for drawer sections ───────────────────────────────────

export const FIELD_CATEGORIES: Record<string, string[]> = {
  "Person Profile": [
    "id",
    "first_name",
    "middle_name",
    "middle_initial",
    "last_name",
    "last_initial",
    "full_name",
    "headline",
    "job_title",
    "primary_email",
    "email_status",
    "photo_url",
    "extrapolated_email_confidence",
    "sex",
    "birth_date",
    "birth_year",
    "interests",
    "skills",
    "industry",
    "seniority",
    "functions",
    "departments",
    "subdepartments",
    "intent_strength",
    "show_intent",
    "is_likely_to_engage",
    "revealed_for_current_team",
    "contact_id",
    "dataset_version",
    "home_owner",
    "married",
    "children",
    "device_type",
    "profile_alias",
    "person",
  ],
  Location: [
    "location_name",
    "location_locality",
    "location_region",
    "location_country",
    "location_continent",
    "location_postal_code",
    "location_street_address",
    "location_address_line_2",
    "location_geo",
    "location_metro",
    "location_last_updated",
    "location_names",
    "regions",
    "countries",
    "street_addresses",
    "formatted_address",
    "time_zone",
  ],
  "Employment History": [
    "experience",
    "job_title_role",
    "job_title_sub_role",
    "job_title_class",
    "job_title_levels",
    "job_start_date",
    "job_last_changed",
    "job_last_verified",
    "education",
  ],
  "Current Company": [
    "job_company_id",
    "job_company_name",
    "job_company_website",
    "job_company_size",
    "job_company_founded",
    "job_company_industry",
    "job_company_linkedin_url",
    "job_company_linkedin_id",
    "job_company_facebook_url",
    "job_company_twitter_url",
    "job_company_location_name",
    "job_company_location_locality",
    "job_company_location_metro",
    "job_company_location_region",
    "job_company_location_country",
    "job_company_location_continent",
    "job_company_location_postal_code",
    "job_company_location_street_address",
    "job_company_location_address_line_2",
    "job_company_location_geo",
  ],
  Organization: [
    "org_id",
    "org_name",
    "org_website_url",
    "org_blog_url",
    "org_phone",
    "org_founded_year",
    "org_description",
    "org_short_description",
    "org_seo_description",
    "org_logo_url",
    "org_angellist_url",
    "org_linkedin_url",
    "org_linkedin_id",
    "org_twitter_url",
    "org_facebook_url",
    "org_publicly_traded_symbol",
    "org_publicly_traded_exchange",
    "org_annual_revenue",
    "org_annual_revenue_printed",
    "org_total_funding",
    "org_total_funding_printed",
    "org_latest_funding_stage",
    "org_latest_funding_date",
    "org_raw_address",
    "org_street_address",
    "org_city",
    "org_state",
    "org_postal_code",
    "org_country",
    "org_location_geo",
    "org_technology_names",
    "org_current_technologies",
    "org_industries",
    "org_secondary_industries",
    "org_sic_codes",
    "org_naics_codes",
    "org_alexa_ranking",
    "org_keywords",
    "org_headcount_growth_6m",
    "org_headcount_growth_12m",
    "org_headcount_growth_24m",
    "org_owned_by_id",
    "org_owned_by_name",
    "org_num_suborganizations",
    "org_funding_events",
    "org_estimated_num_employees",
    "org_primary_domain",
  ],
  "Contact Information": [
    "work_email",
    "recommended_personal_email",
    "personal_emails",
    "contact_emails",
    "contact_email_status",
    "contact_email_sha256",
    "contact_email_md5",
    "contact_email_free_domain",
    "contact_email_position",
    "mobile_phone",
    "phone_numbers",
    "phone_dnc_status",
    "phone_dialer_flags",
    "phone_country_name",
    "phone_country_enabled",
    "phone_potential_high_risk",
  ],
  "Online Profiles": [
    "profiles",
    "linkedin_url",
    "linkedin_username",
    "linkedin_id",
    "facebook_url",
    "facebook_username",
    "facebook_id",
    "twitter_url",
    "twitter_username",
    "github_url",
    "github_username",
  ],
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function getDefaultEnrichmentConfig(): BrandEnrichmentConfig {
  const fields: Record<string, EnrichmentFieldEntry> = {};

  for (const [fieldKey, tier] of Object.entries(ENRICHMENT_FIELD_TIER_MAP)) {
    fields[fieldKey] = { tier, enabled: true };
  }

  return {
    tiers: {
      INCLUDED: true,
      CONSENT_FOR_FULL_RESOLUTION: true,
      EXPLICIT_CONSENT_REQUIRED: true,
    },
    fields,
  };
}

export function formatFieldName(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getEnabledFields(config: BrandEnrichmentConfig): string[] {
  return Object.entries(config.fields)
    .filter(([, entry]) => config.tiers[entry.tier] && entry.enabled)
    .map(([key]) => key);
}

export function getFieldsByTier(
  config: BrandEnrichmentConfig,
  tier: EnrichmentFieldTier
): { key: string; enabled: boolean }[] {
  return Object.entries(config.fields)
    .filter(([, entry]) => entry.tier === tier)
    .map(([key, entry]) => ({ key, enabled: entry.enabled }));
}

export function getFieldsByCategory(
  config: BrandEnrichmentConfig
): Record<string, { key: string; enabled: boolean }[]> {
  const result: Record<string, { key: string; enabled: boolean }[]> = {};

  for (const [category, fieldKeys] of Object.entries(FIELD_CATEGORIES)) {
    const fields = fieldKeys
      .filter((key) => config.fields[key])
      .map((key) => ({
        key,
        enabled: config.fields[key].enabled,
      }));
    if (fields.length > 0) {
      result[category] = fields;
    }
  }

  return result;
}

